import { AxiosResponse } from 'axios'
import { actions } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import { addNotification } from 'store/notifications'
import { getReadableErrorMessage } from 'utils/errorUtils'
import { AssignNetSuiteAssetRequest, NetSuiteAsset } from 'types'

export const fetchContractLineAssets = (contractLineNSId: number) => {
  return async function (dispatch) {
    dispatch(actions.fetchingContractLineAssets())

    if (!contractLineNSId) {
      dispatch(
        actions.fetchingContractLineAssetsError(
          'Contract Line NS ID is required',
        ),
      )
      return
    }

    dispatch(actions.setContractLineId(contractLineNSId))

    try {
      const response = await ssapi.getNetSuiteAssets({ contractLineNSId })
      if (response.status === 204 || response.data.length === 0) {
        dispatch(
          actions.fetchingContractLineAssetsError(
            'No assets found for this contract line',
          ),
        )
      } else {
        const data = response?.data
        dispatch(actions.fetchingContractLineAssetsSuccess(data))

        // Fetch additional data based on the assets
        const assignedAssets = data.filter(asset => asset.site !== null)
        const unassignedAssets = data.filter(asset => asset.site === null)
        const managerNetSuiteCustomerId =
          data.length > 0 ? data[0].managerNetSuiteCustomerId : null

        const assetToCheck =
          assignedAssets.length > 0
            ? assignedAssets[0]
            : unassignedAssets.length > 0
            ? unassignedAssets[0]
            : null

        if (assetToCheck && assetToCheck.adminEmail) {
          try {
            const profileResponse = await ssapi.searchProfiles({
              email: assetToCheck.adminEmail,
            })
            if (profileResponse.data && profileResponse.data.length > 0) {
              const setupAdmin = profileResponse.data[0]
              dispatch(actions.setSetupAdminEmailUserId(setupAdmin.id))
            }
          } catch {
            // Error fetching setup admin user ID - silently continue
          }
        }

        // Fetch manager NetSuite customer data
        if (managerNetSuiteCustomerId) {
          try {
            const customerResponse = await ssapi.getNetSuiteCustomers({
              netSuiteCustomerId: managerNetSuiteCustomerId,
            })
            if (customerResponse.data && customerResponse.data.length > 0) {
              const customerData = customerResponse.data[0]
              dispatch(
                actions.setManagerNetSuiteCustomerData({
                  name: customerData.districtName,
                  nsId: customerData.nsCustomerId,
                }),
              )
            }
          } catch {
            // Error fetching manager NetSuite customer data - silently continue
          }
        }
      }
    } catch (err) {
      const errorMessage = getReadableErrorMessage(
        err,
        'Failed to fetch contract line assets. Please try again later.',
      )
      dispatch(actions.fetchingContractLineAssetsError(errorMessage))
    }
  }
}

export const updatedContractLineAsset = (
  assetId: number,
  asset: AssignNetSuiteAssetRequest,
) => {
  return async function (dispatch) {
    dispatch(actions.updatingContractLineAsset())
    try {
      const response: AxiosResponse<NetSuiteAsset> =
        await ssapi.updateNetSuiteAsset(assetId, asset)
      dispatch(actions.updatingContractLineAssetSuccess(response.data))
      addNotification({
        color: 'success',
        text: 'License updated successfully',
      })
    } catch (err) {
      const errorMessage = getReadableErrorMessage(
        err,
        'License assignment failed. Please try again later.',
      )
      dispatch(actions.updatingContractLineAssetError(errorMessage))
      dispatch(addNotification({ color: 'danger', text: errorMessage }))
      throw err // Re-throw the error so it can be caught by the caller
    }
  }
}

export const clearContractLineAssets = () => {
  return async function (dispatch) {
    dispatch(actions.clearContractLineAssets())
  }
}
